import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Card,
  Tooltip,
  Button,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  Badge,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  useMediaQuery,
  CircularProgress,
  InputLabel,
  Chip,
  Divider,
  Container,
  useTheme,
  Avatar,
  Stack,
  InputAdornment,
  FormHelperText,
  CardContent,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slide,
  Alert,
  TablePagination,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SecurityIcon from "@mui/icons-material/Security";
import PeopleIcon from "@mui/icons-material/People";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WarningIcon from "@mui/icons-material/Warning";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FilterListIcon from "@mui/icons-material/FilterList";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonIcon from "@mui/icons-material/Person";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";

// Fixed import path
import AdminDashboardVM from "../viewmodal/AdminDashboardVM";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// FIXED: Move StatsCards outside to prevent recreation
const StatsCards = React.memo(({ approvers, gatekeepers, visitors, attendanceStats }) => (
  <Grid container spacing={3} sx={{ mb: 4 }}>
    <Grid item xs={12} sm={6} md={3}>
      <Card 
        elevation={2}
        sx={{ 
          p: 3,
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1, color: '#333' }}>
              {approvers?.length || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Approvers
            </Typography>
          </Box>
          <AdminPanelSettingsIcon sx={{ fontSize: 40, color: '#1976d2' }} />
        </Box>
      </Card>
    </Grid>

    <Grid item xs={12} sm={6} md={3}>
      <Card 
        elevation={2}
        sx={{ 
          p: 3,
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1, color: '#333' }}>
              {gatekeepers?.length || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Gatekeepers
            </Typography>
          </Box>
          <SecurityIcon sx={{ fontSize: 40, color: '#1976d2' }} />
        </Box>
      </Card>
    </Grid>

    <Grid item xs={12} sm={6} md={3}>
      <Card 
        elevation={2}
        sx={{ 
          p: 3,
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1, color: '#333' }}>
              {visitors?.length || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Visitors
            </Typography>
          </Box>
          <PeopleIcon sx={{ fontSize: 40, color: '#1976d2' }} />
        </Box>
      </Card>
    </Grid>

    <Grid item xs={12} sm={6} md={3}>
      <Card 
        elevation={2}
        sx={{ 
          p: 3,
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1, color: '#333' }}>
              {attendanceStats?.todayPresent || visitors?.filter(v => v.check_in && !v.check_out)?.length || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Today's Present
            </Typography>
          </Box>
          <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50' }} />
        </Box>
      </Card>
    </Grid>
  </Grid>
));

// Attendance Table Component
const AttendanceTable = React.memo(({ 
  attendanceData, 
  loading, 
  pagination, 
  onPageChange, 
  onRowsPerPageChange,
  filters,
  onFilterChange,
  formatDateTime,
  formatDuration
}) => {
  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        overflow: "hidden",
      }}
    >
      {/* Filters */}
      <CardContent sx={{ pb: 2, bgcolor: "#fafafa" }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            type="date"
            label="Date"
            value={filters.date}
            onChange={(e) => onFilterChange('date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>User Type</InputLabel>
            <Select
              value={filters.userType}
              onChange={(e) => onFilterChange('userType', e.target.value)}
              label="User Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="employee">Employee</MenuItem>
              <MenuItem value="visitor">Visitor</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => onFilterChange('reset')}
          >
            Reset Filters
          </Button>
        </Box>
      </CardContent>
      
      {loading && <LinearProgress />}

      <TableContainer sx={{ maxHeight: "60vh" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                User Info
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                Date
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                First Entry
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                Last Exit
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                Total Hours
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                Entries
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                Device
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Loading attendance data...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : attendanceData.length > 0 ? (
              attendanceData.map((record, index) => (
                <TableRow 
                  key={`${record.id}-${index}`}
                  sx={{ "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" } }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: record.user_type === 'visitor' ? '#ff9800' : '#1976d2',
                          width: 36,
                          height: 36,
                          mr: 1.5,
                          fontSize: "0.875rem",
                        }}
                      >
                        {record.employee_name ? record.employee_name.charAt(0).toUpperCase() : "?"}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {record.employee_name || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {record.user_id} • {record.user_type}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(record.attendance_date).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {record.first_entry ? (
                      <Box>
                        <Typography variant="body2">
                          {formatDateTime(record.first_entry)}
                        </Typography>
                        {record.temperature && (
                          <Typography variant="caption" color="text.secondary">
                            Temp: {record.temperature}°C
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No entry
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {record.last_exit ? (
                      <Typography variant="body2">
                        {formatDateTime(record.last_exit)}
                      </Typography>
                    ) : (
                      <Chip 
                        label="Still in" 
                        size="small" 
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDuration(record.total_hours)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={record.total_entries}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={record.status}
                      size="small"
                      color={record.status === 'Present' ? 'success' : 'error'}
                      variant={record.status === 'Present' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DeviceHubIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption">
                        {record.device_id || 'Unknown'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Box 
                    sx={{ 
                      display: "flex", 
                      flexDirection: "column", 
                      alignItems: "center", 
                      gap: 2 
                    }}
                  >
                    <InfoIcon fontSize="large" sx={{ color: "text.secondary" }} />
                    <Typography variant="h6" color="text.secondary">
                      No Attendance Records Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: "center" }}>
                      No attendance data available for the selected filters.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {attendanceData.length > 0 && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={Math.floor(pagination.offset / pagination.limit)}
          onPageChange={onPageChange}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[25, 50, 100]}
          sx={{ borderTop: "1px solid #e0e0e0" }}
        />
      )}
    </Card>
  );
});

// FIXED: Move AdminHeader outside to prevent recreation
const AdminHeader = React.memo(({
  approvers,
  gatekeepers,
  visitors,
  attendanceStats,
  selectedRole,
  handleRoleChange,
  selectedFile,
  fileName,
  fileInputRef,
  clearFileInput,
  handleFileChange,
  loading,
  handleFileUpload,
  handleSampleFileDownload,
  successMessage,
  errorMessage,
  setSuccessMessage,
  setErrorMessage,
  isMobile
}) => (
  <Box sx={{ mb: 4 }}>
    {/* Page Title */}
    <Box sx={{ mb: 4, textAlign: 'center' }}>
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: "bold",
          color: '#333',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <DashboardIcon sx={{ mr: 2, color: '#1976d2' }} />
        Admin Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Manage users, upload data, monitor visitor activities, and track attendance
      </Typography>
    </Box>

    {/* Statistics Cards */}
    <StatsCards 
      approvers={approvers}
      gatekeepers={gatekeepers}
      visitors={visitors}
      attendanceStats={attendanceStats}
    />
    
    <Card 
      elevation={2}
      sx={{ 
        mb: 3,
        borderRadius: 2,
        border: '1px solid #e0e0e0',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 3, 
            color: '#333', 
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
          }}
        >
          <GroupAddIcon sx={{ mr: 1 }} /> 
          User Management
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        <Grid 
          container 
          spacing={2} 
          alignItems="center" 
          justifyContent="flex-start"
        >
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined" size={isMobile ? "small" : "medium"}>
              <InputLabel id="role-select-label">Select Role</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                value={selectedRole}
                onChange={handleRoleChange}
                label="Select Role"
              >
                <MenuItem value="" disabled>Select Role</MenuItem>
                <MenuItem value="Approver">Approver</MenuItem>
                <MenuItem value="Gatekeeper">Gatekeeper</MenuItem>
              </Select>
              <FormHelperText>
                {!selectedRole && "Select a role to enable file upload/download"}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<UploadFileIcon />}
              sx={{ 
                py: 1.5, 
                textTransform: "none",
                justifyContent: "flex-start",
                color: selectedFile ? 'primary.main' : 'text.secondary',
                borderColor: selectedFile ? 'primary.main' : 'grey.300',
              }}
              disabled={!selectedRole}
            >
              {fileName}
              <input
                ref={fileInputRef}
                id="file-upload"
                type="file"
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept=".csv,.xlsx,.xls"
              />
            </Button>
            {selectedFile && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Size: {(selectedFile.size / 1024).toFixed(1)} KB
                </Typography>
                <IconButton
                  size="small"
                  onClick={clearFileInput}
                  sx={{ p: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleFileUpload}
              disabled={loading || !selectedRole || !selectedFile}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />}
              sx={{ 
                py: 1.5, 
                textTransform: "none",
              }}
            >
              {loading ? "Uploading..." : "Upload User Data"}
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={handleSampleFileDownload}
              disabled={!selectedRole}
              startIcon={<FileDownloadIcon />}
              sx={{ 
                py: 1.5, 
                textTransform: "none",
              }}
            >
              {selectedRole ? `Download Template` : "Select Role First"}
            </Button>
          </Grid>
        </Grid>
        
        {/* Upload Results Display */}
        {(successMessage || errorMessage) && (
          <Box sx={{ mt: 3 }}>
            {successMessage && (
              <Alert 
                severity="success" 
                onClose={() => setSuccessMessage("")}
                sx={{ mb: 1 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {successMessage}
                </Typography>
              </Alert>
            )}
            {errorMessage && (
              <Alert 
                severity="error" 
                onClose={() => setErrorMessage("")}
                sx={{ mb: 1 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {errorMessage}
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  </Box>
));

// FIXED: Move ConfirmationDialog outside to prevent recreation
const ConfirmationDialog = React.memo(({ confirmDialog, setConfirmDialog, handleConfirmDelete }) => (
  <Dialog
    open={confirmDialog.open}
    TransitionComponent={Transition}
    keepMounted
    onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
    PaperProps={{
      sx: {
        borderRadius: 2,
        minWidth: 400,
      }
    }}
  >
    <DialogTitle sx={{ 
      display: 'flex', 
      alignItems: 'center',
      color: '#d32f2f',
      fontWeight: 600
    }}>
      <WarningIcon sx={{ mr: 1.5 }} />
      Confirm Deletion
    </DialogTitle>
    <DialogContent sx={{ pt: 2 }}>
      <DialogContentText sx={{ fontSize: '1rem', color: 'text.primary' }}>
        {confirmDialog.message}
      </DialogContentText>
    </DialogContent>
    <DialogActions sx={{ p: 2 }}>
      <Button 
        onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
        variant="outlined"
        sx={{ 
          textTransform: 'none',
          px: 3
        }}
      >
        Cancel
      </Button>
      <Button 
        onClick={handleConfirmDelete} 
        variant="contained"
        color="error"
        sx={{ 
          textTransform: 'none',
          px: 3,
          ml: 2
        }}
      >
        Delete
      </Button>
    </DialogActions>
  </Dialog>
));

const AdminDashboardView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Attendance-specific state
  const [attendanceData, setAttendanceData] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendancePagination, setAttendancePagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  });
  const [attendanceFilters, setAttendanceFilters] = useState({
    date: '',
    userType: 'all'
  });
  const [attendanceStats, setAttendanceStats] = useState({
    todayPresent: 0,
    totalHoursToday: 0,
    avgHoursToday: 0
  });

  const {
    // Dashboard state
    selectedRole,
    approvers,
    gatekeepers,
    visitors,
    loading,

    // Countries
    countries,

    // File state and methods (updated)
    selectedFile,
    fileName,
    fileInputRef,
    clearFileInput,
    handleFileChange,
    handleRoleChange,
    handleFileUpload,
    handleSampleFileDownload,

    // IconTabs state and methods
    setShowNewRow,
    activeTab,
    newUser,
    showNewRow,
    getActiveTabData,
    handleViewDetails,
    handleDelete,
    handleChangeTab,
    handleInputChange,
    handleAddRow,
    handleSaveUser,
    formatDateTime,

    // Refresh function
    refreshData,

    // Search and filtering
    searchText,
    setSearchText,
    handleSearchChange,
    filteredData,
    validationErrors,
    successMessage,
    errorMessage,
    setSuccessMessage,
    setErrorMessage,
    closeAlerts,

    // Confirmation dialog
    confirmDialog,
    setConfirmDialog,
    handleConfirmDelete,
  } = AdminDashboardVM();

  // Helper function to format duration
  const formatDuration = (hours) => {
    if (!hours || hours === 0) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Fetch attendance data
// Replace the fetchAttendanceData function in your AdminDashboardView.js with this corrected version:

const fetchAttendanceData = async () => {
  setAttendanceLoading(true);
  try {
    const params = new URLSearchParams({
      limit: attendancePagination.limit.toString(),
      offset: attendancePagination.offset.toString(),
    });

    if (attendanceFilters.date) {
      params.append('date', attendanceFilters.date);
    }
    if (attendanceFilters.userType !== 'all') {
      params.append('user_type', attendanceFilters.userType);
    }

    // Fix: Use the correct base URL like other API calls in your app
    const response = await fetch(`${process.env.REACT_APP_BE_URL2}/visitor-mgmt/attendance-data?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    console.log('Attendance API Response Status:', response.status);
    console.log('Attendance API Response URL:', response.url);

    if (response.ok) {
      const result = await response.json();
      console.log('Attendance API Result:', result);
      
      if (result.success) {
        setAttendanceData(result.data.attendance);
        setAttendancePagination(result.data.pagination);
        
        // Calculate stats
        const todayData = result.data.attendance.filter(record => 
          new Date(record.attendance_date).toDateString() === new Date().toDateString()
        );
        setAttendanceStats({
          todayPresent: todayData.filter(r => r.status === 'Present').length,
          totalHoursToday: todayData.reduce((sum, r) => sum + (r.total_hours || 0), 0),
          avgHoursToday: todayData.length > 0 ? 
            todayData.reduce((sum, r) => sum + (r.total_hours || 0), 0) / todayData.length : 0
        });
      } else {
        console.error('API returned success:false', result);
        throw new Error(result.message || 'Failed to fetch attendance data');
      }
    } else {
      const errorText = await response.text();
      console.error('HTTP Error Response:', errorText);
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error fetching attendance data:', error);
    
    // Show user-friendly error message
    if (error.message.includes('404') || error.message.includes('Not Found')) {
      console.error('Attendance API endpoint not found. Make sure the backend routes are properly set up.');
    } else if (error.message.includes('500')) {
      console.error('Server error. Check the backend logs.');
    } else if (error.message.includes('fetch')) {
      console.error('Network error. Check if the backend server is running.');
    }
    
    // Set empty data to prevent UI issues
    setAttendanceData([]);
    setAttendancePagination(prev => ({ ...prev, total: 0 }));
    setAttendanceStats({
      todayPresent: 0,
      totalHoursToday: 0,
      avgHoursToday: 0
    });
  } finally {
    setAttendanceLoading(false);
  }
};

  // Handle attendance filter changes
  const handleAttendanceFilterChange = (filterName, value) => {
    if (filterName === 'reset') {
      setAttendanceFilters({ date: '', userType: 'all' });
      setAttendancePagination(prev => ({ ...prev, offset: 0 }));
    } else {
      setAttendanceFilters(prev => ({ ...prev, [filterName]: value }));
      setAttendancePagination(prev => ({ ...prev, offset: 0 }));
    }
  };

  // Handle pagination
  const handleAttendancePageChange = (event, newPage) => {
    setAttendancePagination(prev => ({
      ...prev,
      offset: newPage * prev.limit
    }));
  };

  const handleAttendanceRowsPerPageChange = (event) => {
    setAttendancePagination(prev => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      offset: 0
    }));
  };

  // Fetch attendance data when filters or pagination change
  useEffect(() => {
    if (activeTab === "Attendance") {
      fetchAttendanceData();
    }
  }, [activeTab, attendanceFilters, attendancePagination.limit, attendancePagination.offset]);

  // FIXED: Stable data reference using useMemo
  const data = useMemo(() => {
    if (activeTab === "Attendance") {
      return attendanceData;
    }
    return filteredData || getActiveTabData || [];
  }, [filteredData, getActiveTabData, activeTab, attendanceData]);

  // FIXED: Move IconTabs inside the main component but make it stable
  const IconTabs = useMemo(() => (
    <Box>
      {/* Tab Navigation */}
      <Card 
        elevation={2} 
        sx={{ 
          mb: 3, 
          borderRadius: 2, 
          border: '1px solid #e0e0e0',
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            bgcolor: "#f5f5f5",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Box 
            sx={{ 
              display: "flex", 
              flexGrow: 1, 
              justifyContent: { xs: "space-between", sm: "flex-start" },
            }}
          >
            <Tooltip title="Approvers" arrow>
              <Button
                onClick={() => handleChangeTab("Approver")}
                color={activeTab === "Approver" ? "primary" : "inherit"}
                sx={{
                  py: 2,
                  px: { xs: 2, sm: 4 },
                  borderRadius: 0,
                  borderBottom: activeTab === "Approver" ? "3px solid #1976d2" : "3px solid transparent",
                  fontWeight: activeTab === "Approver" ? 600 : 400,
                  textTransform: "none",
                }}
                startIcon={
                  <Badge 
                    badgeContent={approvers?.length || 0} 
                    color="primary"
                  >
                    <AdminPanelSettingsIcon />
                  </Badge>
                }
              >
                {!isMobile && "Approvers"}
              </Button>
            </Tooltip>

            <Tooltip title="Gatekeepers" arrow>
              <Button
                onClick={() => handleChangeTab("Gatekeeper")}
                color={activeTab === "Gatekeeper" ? "primary" : "inherit"}
                sx={{
                  py: 2,
                  px: { xs: 2, sm: 4 },
                  borderRadius: 0,
                  borderBottom: activeTab === "Gatekeeper" ? "3px solid #1976d2" : "3px solid transparent",
                  fontWeight: activeTab === "Gatekeeper" ? 600 : 400,
                  textTransform: "none",
                }}
                startIcon={
                  <Badge 
                    badgeContent={gatekeepers?.length || 0} 
                    color="primary"
                  >
                    <SecurityIcon />
                  </Badge>
                }
              >
                {!isMobile && "Gatekeepers"}
              </Button>
            </Tooltip>

            <Tooltip title="Visitors" arrow>
              <Button
                onClick={() => handleChangeTab("Visitor")}
                color={activeTab === "Visitor" ? "primary" : "inherit"}
                sx={{
                  py: 2,
                  px: { xs: 2, sm: 4 },
                  borderRadius: 0,
                  borderBottom: activeTab === "Visitor" ? "3px solid #1976d2" : "3px solid transparent",
                  fontWeight: activeTab === "Visitor" ? 600 : 400,
                  textTransform: "none",
                }}
                startIcon={
                  <Badge 
                    badgeContent={visitors?.length || 0} 
                    color="primary"
                  >
                    <PeopleIcon />
                  </Badge>
                }
              >
                {!isMobile && "Visitors"}
              </Button>
            </Tooltip>

            <Tooltip title="Attendance" arrow>
              <Button
                onClick={() => handleChangeTab("Attendance")}
                color={activeTab === "Attendance" ? "primary" : "inherit"}
                sx={{
                  py: 2,
                  px: { xs: 2, sm: 4 },
                  borderRadius: 0,
                  borderBottom: activeTab === "Attendance" ? "3px solid #1976d2" : "3px solid transparent",
                  fontWeight: activeTab === "Attendance" ? 600 : 400,
                  textTransform: "none",
                }}
                startIcon={
                  <Badge 
                    badgeContent={attendanceData?.length || 0} 
                    color="primary"
                  >
                    <AccessTimeIcon />
                  </Badge>
                }
              >
                {!isMobile && "Attendance"}
              </Button>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", px: 2 }}>
            <Tooltip title="Refresh Data" arrow>
              <IconButton 
                onClick={() => {
                  refreshData();
                  if (activeTab === "Attendance") {
                    fetchAttendanceData();
                  }
                }} 
                color="primary" 
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {/* Render different content based on active tab */}
        {activeTab === "Attendance" ? (
          // Attendance Tab Content
          <AttendanceTable
            attendanceData={attendanceData}
            loading={attendanceLoading}
            pagination={attendancePagination}
            onPageChange={handleAttendancePageChange}
            onRowsPerPageChange={handleAttendanceRowsPerPageChange}
            filters={attendanceFilters}
            onFilterChange={handleAttendanceFilterChange}
            formatDateTime={formatDateTime}
            formatDuration={formatDuration}
          />
        ) : (
          // Original User Management Content
          <>
            {/* Search and Action Bar */}
            <Box 
              sx={{ 
                p: 2, 
                display: "flex",
                alignItems: "center",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                gap: 2,
                bgcolor: "#fafafa",
              }}
            >
              <Box 
                sx={{ 
                  display: "flex", 
                  alignItems: "center",
                  width: { xs: "100%", sm: "auto" }
                }}
              >
                <TextField
                  placeholder={`Search ${activeTab}s...`}
                  variant="outlined"
                  size="small"
                  value={searchText}
                  onChange={handleSearchChange}
                  sx={{ width: { xs: "100%", sm: 300 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", width: { xs: "100%", sm: "auto" } }}>
                {(activeTab === "Approver" || activeTab === "Gatekeeper") && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddRow}
                    sx={{
                      textTransform: "none",
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    Add {activeTab}
                  </Button>
                )}
              </Box>
            </Box>

            {/* Table Section */}
            <Card
              elevation={2}
              sx={{
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                overflow: "hidden",
              }}
            >
              {loading && <LinearProgress />}

              <TableContainer sx={{ overflowX: "auto", maxHeight: "60vh" }}>
                <Table stickyHeader sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                        User Details
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                        Contact Info
                      </TableCell>
                      {activeTab === "Visitor" && (
                        <>
                          <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                            Check In
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                            Check Out
                          </TableCell>
                        </>
                      )}
                      <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {showNewRow && (
                      <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                        <TableCell>
                          <TextField
                            variant="outlined"
                            name="full_name"
                            value={newUser.full_name}
                            onChange={handleInputChange}
                            fullWidth
                            size="small"
                            placeholder="Enter full name"
                            error={!!validationErrors.full_name}
                            helperText={validationErrors.full_name}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack spacing={1}>
                            {/* Country Code and Contact Number Row */}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <FormControl sx={{ minWidth: 120 }} size="small">
                                <InputLabel>Country</InputLabel>
                                <Select
                                  name="country_code"
                                  value={newUser.country_code}
                                  onChange={handleInputChange}
                                  label="Country"
                                  error={!!validationErrors.country_code}
                                >
                                  {countries.map((country) => (
                                    <MenuItem key={country.country_code} value={country.dial_code}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2">
                                          {country.country_code}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {country.dial_code}
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                                {validationErrors.country_code && (
                                  <FormHelperText error>{validationErrors.country_code}</FormHelperText>
                                )}
                              </FormControl>
                              
                              <TextField
                                variant="outlined"
                                name="contact_number"
                                value={newUser.contact_number}
                                onChange={handleInputChange}
                                fullWidth
                                size="small"
                                placeholder="10-digit number"
                                error={!!validationErrors.contact_number}
                                helperText={validationErrors.contact_number}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Typography variant="body2" color="text.secondary">
                                        {newUser.country_code}
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Box>
                            
                            <TextField
                              variant="outlined"
                              name="email"
                              value={newUser.email}
                              onChange={handleInputChange}
                              fullWidth
                              size="small"
                              placeholder="Email address"
                              error={!!validationErrors.email}
                              helperText={validationErrors.email}
                            />
                          </Stack>
                        </TableCell>
                        {activeTab === "Visitor" && (
                          <>
                            <TableCell />
                            <TableCell />
                          </>
                        )}
                        <TableCell align="center">
                          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              startIcon={<SaveIcon />}
                              onClick={handleSaveUser}
                              sx={{ textTransform: "none" }}
                            >
                              Save
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => setShowNewRow(false)}
                              sx={{ textTransform: "none" }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}

                    {loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={activeTab === "Visitor" ? 5 : 3}
                          align="center"
                          sx={{ py: 4 }}
                        >
                          <CircularProgress size={40} />
                          <Typography variant="body2" sx={{ mt: 2 }}>
                            Loading data...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : data.length > 0 ? (
                      data.map((user) => (
                        <TableRow 
                          key={user.id}
                          sx={{ 
                            "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Avatar 
                                sx={{ 
                                  bgcolor: "#1976d2",
                                  width: 36,
                                  height: 36,
                                  mr: 1.5,
                                  fontSize: "0.875rem",
                                }}
                              >
                                {user.full_name ? user.full_name.charAt(0).toUpperCase() : "?"}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {user.full_name || "N/A"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {user.id}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  {user.contact_number || user.phone || user.mobile_number || user.mobile || "N/A"}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  {user.email || "N/A"}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          {activeTab === "Visitor" && (
                            <>
                              <TableCell>
                                {user.check_in || user.checkin_time || user.check_in_time ? (
                                  <Chip
                                    label={formatDateTime(user.check_in || user.checkin_time || user.check_in_time)}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Chip 
                                    label="Not checked in" 
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {user.check_out || user.checkout_time || user.check_out_time ? (
                                  <Chip
                                    label={formatDateTime(user.check_out || user.checkout_time || user.check_out_time)}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Chip 
                                    label="Not checked out" 
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                            </>
                          )}
                          <TableCell align="center">
                            <Tooltip title="Delete User" arrow>
                              <IconButton
                                onClick={() => handleDelete(user.id, activeTab, user.full_name)}
                                color="error"
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={activeTab === "Visitor" ? 5 : 3}
                          align="center"
                          sx={{ py: 6 }}
                        >
                          <Box 
                            sx={{ 
                              display: "flex", 
                              flexDirection: "column", 
                              alignItems: "center", 
                              gap: 2 
                            }}
                          >
                            <InfoIcon fontSize="large" sx={{ color: "text.secondary" }} />
                            <Typography variant="h6" color="text.secondary">
                              No {activeTab}s Found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: "center" }}>
                              {searchText ? 
                                `No ${activeTab}s match your search criteria "${searchText}".` : 
                                `There are no ${activeTab}s in the system yet.`
                              }
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {data.length > 0 && activeTab !== "Attendance" && (
                <Box 
                  sx={{ 
                    p: 2, 
                    display: "flex", 
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderTop: "1px solid #e0e0e0",
                    bgcolor: "#fafafa",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing {data.length} {data.length === 1 ? activeTab.toLowerCase() : `${activeTab.toLowerCase()}s`}
                    {searchText && ` matching "${searchText}"`}
                  </Typography>
                </Box>
              )}
            </Card>
          </>
        )}
      </Card>
    </Box>
  ), [
    activeTab,
    approvers,
    gatekeepers,
    visitors,
    attendanceData,
    isMobile,
    handleChangeTab,
    refreshData,
    fetchAttendanceData,
    searchText,
    handleSearchChange,
    handleAddRow,
    loading,
    attendanceLoading,
    data,
    showNewRow,
    newUser,
    handleInputChange,
    validationErrors,
    countries,
    handleSaveUser,
    setShowNewRow,
    formatDateTime,
    formatDuration,
    handleDelete,
    attendancePagination,
    attendanceFilters,
    handleAttendancePageChange,
    handleAttendanceRowsPerPageChange,
    handleAttendanceFilterChange
  ]); // Dependencies for useMemo

  // Security measures (same as visitor approval component)
// Security measures (same as visitor approval component)
useEffect(() => {
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  const handleKeyDown = (e) => {
    // if (e.key === 'F12' || 
    //     (e.ctrlKey && e.shiftKey && e.key === 'I') ||
    //     (e.ctrlKey && e.shiftKey && e.key === 'J') ||
    //     (e.ctrlKey && e.key === 'u') ||
    //     (e.ctrlKey && e.shiftKey && e.key === 'C') ||
    //     (e.ctrlKey && e.key === 's') ||
    //     (e.ctrlKey && e.key === 'a') ||
    //     (e.ctrlKey && e.key === 'p')) {
    //   e.preventDefault();
    //   return false;
    // }
  };

  const handleSelectStart = (e) => {
    e.preventDefault();
    return false;
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    return false;
  };

  document.addEventListener('contextmenu', handleContextMenu);
  document.addEventListener('keydown', handleKeyDown);
  document.addEventListener('selectstart', handleSelectStart);
  document.addEventListener('dragstart', handleDragStart);

  return () => {
    document.removeEventListener('contextmenu', handleContextMenu);
    document.removeEventListener('keydown', handleKeyDown);
    document.removeEventListener('selectstart', handleSelectStart);
    document.removeEventListener('dragstart', handleDragStart);
  };
}, []);

  return (
    <Box 
      sx={{ 
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        py: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 3 },
        mt: { xs: 10, sm: 12 },
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <Container maxWidth="xl">
        <AdminHeader 
          approvers={approvers}
          gatekeepers={gatekeepers}
          visitors={visitors}
          attendanceStats={attendanceStats}
          selectedRole={selectedRole}
          handleRoleChange={handleRoleChange}
          selectedFile={selectedFile}
          fileName={fileName}
          fileInputRef={fileInputRef}
          clearFileInput={clearFileInput}
          handleFileChange={handleFileChange}
          loading={loading}
          handleFileUpload={handleFileUpload}
          handleSampleFileDownload={handleSampleFileDownload}
          successMessage={successMessage}
          errorMessage={errorMessage}
          setSuccessMessage={setSuccessMessage}
          setErrorMessage={setErrorMessage}
          isMobile={isMobile}
        />
        {IconTabs}
        <ConfirmationDialog 
          confirmDialog={confirmDialog}
          setConfirmDialog={setConfirmDialog}
          handleConfirmDelete={handleConfirmDelete}
        />
      </Container>
    </Box>
  );
};

export default AdminDashboardView;